/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import {
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    TextField,
} from "@material-ui/core"
import { ChangeEvent, Fragment } from "react"
import { UserCreateError } from "../userTypes"
import { Theme } from "../../types"
import { useUserDispatch, useUserState } from "../../../context/UserContext"

type Props = {
    isRegistered: boolean
    error?: UserCreateError
    missingRequiredField: boolean
    onAttributeChange: () => void
    invalidSalesEmail?: boolean
}

function ContactInfoEditor(props: Props) {
    const { companyName, email, password, salesContactEmail, category } =
        useUserState()
    const dispatch = useUserDispatch()

    const handleAttributeChange = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch({
            type: "UPDATE_ATTRIBUTE",
            payload: {
                key: event.target.name,
                value: event.target.value,
            },
        })
        props.onAttributeChange()
    }

    return (
        <Fragment>
            <h3>Kapcsolat</h3>
            <div css={styles.inputsContainer}>
                <TextField
                    name="companyName"
                    value={companyName}
                    label="Cégnév"
                    required
                    variant="outlined"
                    error={props.missingRequiredField}
                    helperText={
                        props.missingRequiredField ? "Hiányzó cégnév" : ""
                    }
                    onChange={handleAttributeChange}
                    css={styles.nameInput}
                    data-testid="input_companyName"
                />
                <h4>Felhasználó</h4>
                <div css={styles.userDataContainer}>
                    <TextField
                        name="email"
                        value={email}
                        type="text"
                        variant="outlined"
                        label="Email"
                        disabled={props.isRegistered}
                        autoComplete={"off"}
                        onChange={handleAttributeChange}
                        error={props.error?.code === "user_exist"}
                        helperText={
                            props.error?.code === "user_exist" &&
                            props.error.message
                        }
                        css={styles.username}
                        data-testid="input_email"
                    />
                    <TextField
                        name="password"
                        value={password}
                        label="Jelszó"
                        type="password"
                        disabled={props.isRegistered}
                        variant="outlined"
                        autoComplete={"off"}
                        onChange={handleAttributeChange}
                        error={props.error?.code === "weak_password"}
                        helperText={
                            props.error?.code === "weak_password"
                                ? props.error.message
                                : "minimum: 8 karakter, 1 szám, 1 nagy- és kisbetű"
                        }
                        css={styles.password}
                        data-testid="input_password"
                    />
                </div>
                <TextField
                    name="salesContactEmail"
                    value={salesContactEmail}
                    label="Értékesítő email"
                    variant="outlined"
                    onChange={handleAttributeChange}
                    error={props.invalidSalesEmail}
                    helperText={
                        props.invalidSalesEmail ? "Hibás email cím" : ""
                    }
                    css={styles.username}
                />
            </div>
            <div>
                <h3>Infók</h3>
                <div css={styles.inputsContainer}>
                    <FormLabel component="legend">Kategória</FormLabel>
                    <RadioGroup
                        aria-label="category"
                        name="category"
                        value={category ?? "none"}
                        onChange={handleAttributeChange}
                    >
                        <FormControlLabel
                            value="user"
                            control={<Radio />}
                            label="Felhasználó"
                        />
                        <FormControlLabel
                            value="retailer"
                            control={<Radio />}
                            label="Kisker"
                        />
                        <FormControlLabel
                            value="none"
                            control={<Radio />}
                            label="Egyéb"
                        />
                    </RadioGroup>
                </div>
            </div>
        </Fragment>
    )
}

export default ContactInfoEditor

const styles = {
    inputsContainer: ({ spaces: { smallScale } }: Theme) => css`
        display: flex;
        flex-direction: column;
        padding: 0 ${smallScale.s} 0 ${smallScale.s};
    `,
    nameInput: (theme: Theme) => css`
        padding-bottom: ${theme.spaces.smallScale.s};
        width: 100%;
    `,
    userDataContainer: (theme: Theme) => css`
        display: flex;
        flex-direction: column;
        width: 100%;
        ${theme.mediaQuery.desktop.default} {
            flex-direction: row;
        }
    `,
    password: (theme: Theme) => css`
        width: 100%;
        padding-bottom: ${theme.spaces.smallScale.s};
    `,
    username: (theme: Theme) => css`
        width: 100%;
        padding-right: ${theme.spaces.smallScale.s};
        padding-bottom: ${theme.spaces.smallScale.s};
    `,
    tag: (theme: Theme) => css`
        width: 90%;
        margin: ${theme.spaces.smallScale.xs};
    `,
}
